import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';

import { BonusStatus } from '../../api/fetchers/bonusPromotions/types/constants';
import { fetchBonusCampaignsAction, postClaimBonusAction } from '../../store/apiActions/fetchBonusPromotionAction';
import { getChipWeightFromIndex, getChipFallbackWeight, getTotalBalanceFromState } from '../../utils/chips';
import { roundNumber } from '../../utils/number';
import { fetchTableConfigAction } from '../apiActions/fetchTableConfigAction';
import { fetchUserInfoAction } from '../apiActions/fetchUserInfoAction';
import type { CoreRootState } from '../CoreStore';
import { betActions } from '../slices/bets/slice';
import { chipActions } from '../slices/chips/slice';
import { ChipTypes } from '../slices/chips/types';
import type { FreeChipValueType } from '../slices/chips/types';
import { gameStateActions } from '../slices/game/slice';
import { GameStatus } from '../slices/game/types';

// This middleware listens for tableConfig.fulfilled and sets game chips on initial game load.
export const chipsMiddleware = createListenerMiddleware<CoreRootState>();

chipsMiddleware.startListening({
  actionCreator: fetchTableConfigAction.fulfilled,
  effect: (_action, { dispatch, getState }) => {
    const state = getState();
    const chipAmounts = state.config.tableConfig?.chipAmounts;
    const chipColors = state.config.tableConfig?.chipColorCodes;

    if (chipAmounts) {
      dispatch(
        chipActions.setCasinoChips(
          chipAmounts.map((value, index) => {
            const weight = getChipWeightFromIndex(index) || getChipFallbackWeight(chipAmounts, value);

            return {
              value,
              weight,
              color: chipColors?.[weight - 1] ?? undefined, // chip component has a color fallback mechanism implemented based on provided weight
              type: ChipTypes.CasinoChip,
            };
          })
        )
      );
    }
  },
});
chipsMiddleware.startListening({
  matcher: isAnyOf(fetchBonusCampaignsAction.fulfilled, postClaimBonusAction.fulfilled),
  effect: (_action, { dispatch, getState }) => {
    const state = getState();
    const activeBonusCampaign = state.bonusPromotions.activeBonusCampaign;
    if (activeBonusCampaign?.bonus.status === BonusStatus.Active) {
      const freeChipObject: FreeChipValueType = {
        value: activeBonusCampaign.bonus.chipValue,
        weight: -1,
        type: ChipTypes.FreeChip,
        count: activeBonusCampaign.bonus.noOfchipsRemaining,
        maxLimit: activeBonusCampaign.bonus.maxBetLimit,
      };
      dispatch(chipActions.setFreeChips([freeChipObject]));
    } else {
      dispatch(chipActions.setFreeChips([]));
    }
  },
});

// This middleware listens for actions that affect the total balance and sets chips as disabled if the total balance is insufficient.
chipsMiddleware.startListening({
  matcher: isAnyOf(
    betActions.resetBetStack,
    betActions.setRawTotalBet,
    betActions.setPlacedBetType,
    fetchBonusCampaignsAction.fulfilled,
    fetchUserInfoAction.fulfilled,
    gameStateActions.setBalance,
    gameStateActions.updateBalance,
    postClaimBonusAction.fulfilled
  ),
  effect: (action, { dispatch, getState }) => {
    const state = getState();
    const { totalBet, placedBetType: betType } = state.bets;
    const { activeBonusCampaign } = state.bonusPromotions;
    const { chipsData } = state.chips;
    const { currencyInfo } = state.gameState;

    // Determine decimal precision and chip values
    const decimalPrecision = currencyInfo?.digitsNumber || 2;
    const freeChipValue = activeBonusCampaign?.bonus.chipValue;
    const totalAcceptedFreeChipCount = freeChipValue ? roundNumber(totalBet / freeChipValue, 0) : 0;

    // Check bet status and chip availability
    const isBetPlaced = totalBet > 0;
    const freeChipPlaced = betType === ChipTypes.FreeChip;
    const isBetsOpen = [GameStatus.PlaceYourBets, GameStatus.LastBets].includes(
      state.gameState.currentStatus as GameStatus
    );
    let availableFreeChips = activeBonusCampaign?.bonus.noOfchipsRemaining ?? 0;
    if (freeChipPlaced && availableFreeChips && isBetsOpen) {
      availableFreeChips -= totalAcceptedFreeChipCount;
    }

    // Determine chip disable states
    const freeChipsDisabled = isBetPlaced && !freeChipPlaced;
    const casinoChipsDisabled = isBetPlaced && freeChipPlaced;

    //Populate updated Free Chip properties
    const freeChipUpdatedProps: { disabled: boolean; disabledReason: string; count?: number } = {
      disabled: freeChipsDisabled,
      disabledReason: freeChipsDisabled ? 'PROMO_MIXING_CHIPS_NOT_ALLOWED' : '',
    };
    if (isBetsOpen) {
      freeChipUpdatedProps.count = availableFreeChips;
    }

    // Calculate total balance
    const totalBalance =
      action.type === fetchUserInfoAction.fulfilled.type
        ? getTotalBalanceFromState(state) // Updated balance from user info
        : roundNumber(getTotalBalanceFromState(state) - totalBet, decimalPrecision); // Adjusted balance with bet deduction

    // Map and update casino chips
    const mappedChips = chipsData.casinoChips.map((chip) => {
      const disabled = casinoChipsDisabled || totalBalance < chip.value;
      let disabledReason = undefined;
      if (disabled) {
        disabledReason = casinoChipsDisabled ? 'PROMO_MIXING_CHIPS_NOT_ALLOWED' : 'INSUFFICIENTFUNDS';
      }

      return { ...chip, disabled, disabledReason };
    });
    // Dispatch updates
    dispatch(chipActions.updateFreeChipsState(freeChipUpdatedProps));
    dispatch(chipActions.setCasinoChips(mappedChips));
  },
});

chipsMiddleware.startListening({
  matcher: isAnyOf(
    chipActions.setCasinoChips,
    chipActions.setFreeChips,
    chipActions.updateFreeChipsState,
    chipActions.updateCasinoChipsState
  ),
  effect: (_action, { dispatch, getState }) => {
    const state = getState();
    const { casinoChips, freeChips } = state.chips.chipsData;

    const mappedChips = [...freeChips, ...casinoChips].slice(0, casinoChips.length);

    dispatch(chipActions.setChips(mappedChips));
  },
});
